import {GetFormattedOfflineOrder} from '@/models/OfflineOrderModel'
import {getOfflineOrder, getOfflineOrderCsvFile} from '@/services/Order/OfflineOrder/offlineOrderList'
import {OfflineOrderListColConfig} from '@/services/MockData/Order/OrderList/OfflineOrderColConfig'
import {useQueries} from '@tanstack/react-query'
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {useDataTable} from '@/hooks/useDataTable-hook'
import {useMemo, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {filterColumnsByFeatures, setDefaultFilterValues} from '@/utils/columnConfigUtils'
import {showOrderTypeFeature} from '@/utils/commons'

export const useOfflineOrderList = () => {
  const {setPageIndex, setSortingObj, setSearchArry, pageIndex, sortingObj, searchArr,resetSearchArr, handleResetAll} = useDataTable()
  const countryCode = useSelector(selectCountryCode) || localStorage.getItem("countryCode"); // Get countryCode from Redux store

  // Filter columns based on feature flags and country code
  const filteredColumns = useMemo(() => {
    const featureFlags = {
      showOrderTypeFeature
    };
    return filterColumnsByFeatures(OfflineOrderListColConfig, countryCode, featureFlags);
  }, [countryCode])

  // Set default filters based on column configuration
  useEffect(() => {
    setDefaultFilterValues(filteredColumns, searchArr, setSearchArry);
  }, [searchArr, setSearchArry, filteredColumns])

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj?.column)
      params.append('order', sortingObj?.direction)
    }
    // Add pagination parameters
    params.append('pageSize', 10)
    params.append('page', pageIndex || 0)

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['OFFLINE_ORDER', queryParams, searchArr],
        queryFn: () => getOfflineOrder(searchArr, queryParams),
      },
    ],
  })

  const onDownloadBtnClick = async selectedIds => {
    const selectedIdsArr = selectedIds.join(',');
    const tmpParams = new URLSearchParams(queryParams)
    tmpParams.delete('pageSize') // Remove pageSize for download
    tmpParams.delete('page') // Remove page for download
    tmpParams.toString()
    const defaultSortingObj =
      Object.keys(tmpParams)?.length > 0
        ? `${tmpParams}`
        : `orderType=OFFLINE`


    const body = {
      ids: selectedIdsArr,
      filters: searchArr,
    }

    const data = await getOfflineOrderCsvFile(body, defaultSortingObj)
    const fileName = `OfflineOrder_${getDateWithTimeString()}`
    downloadCSVFiles(data, fileName)
  }

  const resultsData = useMemo(() => data || {}, [data])
  const offlineOrderData = useMemo(() => GetFormattedOfflineOrder(resultsData), [resultsData])

  return {
    sortingObj,
    searchArr,
    offlineOrderData,
    OfflineOrderListColConfigData: filteredColumns, // Return filtered columns
    pageIndex,
    isLoading: isLoading || isFetching,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    resetSearchArr,
    handleResetAll
  }
}
